import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, impersonationAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeContext, setActiveContext] = useState(null);
  const [availableContexts, setAvailableContexts] = useState([]);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          const response = await authAPI.me();
          const userData = response.data;
          setUser(userData);
          setAvailableContexts(userData.available_contexts || []);
          setActiveContext(userData.active_context || null);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (err) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const applySession = (data) => {
    const { access_token, user: userData, device_token } = data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (device_token) localStorage.setItem('kr_device_token', device_token);
    setUser(userData);
    setAvailableContexts(userData.available_contexts || []);
    setActiveContext(userData.active_context || null);
    return userData;
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login(email, password);
      // Two-factor challenge — caller must complete via completeTwoFactor.
      if (response.data && response.data.two_factor_required) {
        return { twoFactorRequired: true, ...response.data };
      }
      return applySession(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let message;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        message = detail[0].msg;
      } else if (!err.response) {
        message = 'We couldn\'t reach the server. Please check your connection and try again.  •  No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';
      } else {
        message = 'The email or password is incorrect. Please try again.  •  El correo o la contraseña son incorrectos. Inténtalo de nuevo.';
      }
      setError(message);
      throw new Error(message);
    }
  };

  const completeTwoFactor = (data) => applySession(data);

  const activate = async (token, email, password) => {
    try {
      setError(null);
      const response = await authAPI.activate(token, email, password);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setAvailableContexts(userData.available_contexts || []);
      setActiveContext(userData.active_context || null);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Activation failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setActiveContext(null);
    setAvailableContexts([]);
  };

  const switchContext = useCallback(async (contextType, contextId) => {
    try {
      const response = await authAPI.switchContext(contextType, contextId);
      const newContext = response.data.active_context;
      setActiveContext(newContext);
      // Refresh user data with new context
      const meResponse = await authAPI.me();
      const userData = meResponse.data;
      setUser(userData);
      setAvailableContexts(userData.available_contexts || []);
      localStorage.setItem('user', JSON.stringify(userData));
      return newContext;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to switch context');
    }
  }, []);

  const refreshUser = async () => {
    try {
      const response = await authAPI.me();
      const userData = response.data;
      setUser(userData);
      setAvailableContexts(userData.available_contexts || []);
      setActiveContext(userData.active_context || null);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (err) {
      logout();
    }
  };

  // ── SuperAdmin Impersonation ("View As") ──
  // Swap the current token for a token whose claims belong to the target
  // (agent or agency principal), then refresh user state.
  const startImpersonation = useCallback(async (target_type, target_id) => {
    const response = await impersonationAPI.start(target_type, target_id);
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    const meResponse = await authAPI.me();
    const userData = meResponse.data;
    setUser(userData);
    setAvailableContexts(userData.available_contexts || []);
    setActiveContext(userData.active_context || null);
    localStorage.setItem('user', JSON.stringify(userData));
    return userData;
  }, []);

  const exitImpersonation = useCallback(async () => {
    const response = await impersonationAPI.exit();
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    const meResponse = await authAPI.me();
    const userData = meResponse.data;
    setUser(userData);
    setAvailableContexts(userData.available_contexts || []);
    setActiveContext(userData.active_context || null);
    localStorage.setItem('user', JSON.stringify(userData));
    return userData;
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    completeTwoFactor,
    logout,
    activate,
    refreshUser,
    switchContext,
    activeContext,
    availableContexts,
    startImpersonation,
    exitImpersonation,
    isImpersonating: !!user?.impersonation?.active,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
